import { useCallback, useState } from "react";
import { LOCAL_STORAGE_KEYS } from "../../../modules/witadmin/Common/constants";

const ID_KEY = LOCAL_STORAGE_KEYS.AI_ASSISTANT_CONVERSATION_ID;
const TICKER_KEY = LOCAL_STORAGE_KEYS.AI_ASSISTANT_CONVERSATION_TICKER;

function generateId(): string {
  if (typeof crypto?.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // getRandomValues is available outside secure contexts, unlike randomUUID.
  if (typeof crypto?.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return `fb-${Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("")}`;
  }
  return `fb-${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

// Reuses the stored id only when it belongs to the SAME property. A ticker mismatch
// (including a first-ever visit, where storedTicker is null) always regenerates —
// this is the client-side half of the property-isolation guarantee; it's what keeps
// a previous property's session id from ever being sent under a different property.
function readOrCreateForTicker(propertyTicker: string): string {
  try {
    const storedTicker = window.localStorage.getItem(TICKER_KEY);
    const storedId = window.localStorage.getItem(ID_KEY);
    if (storedTicker === propertyTicker && storedId) {
      return storedId;
    }
    const id = generateId();
    window.localStorage.setItem(ID_KEY, id);
    window.localStorage.setItem(TICKER_KEY, propertyTicker);
    return id;
  } catch {
    return generateId();
  }
}

export function useConversationId(
  propertyTicker: string
): [string, () => void, (id: string) => void] {
  const [conversationId, setConversationId] = useState<string>(() =>
    readOrCreateForTicker(propertyTicker)
  );

  const resetConversationId = useCallback(() => {
    try {
      const id = generateId();
      window.localStorage.setItem(ID_KEY, id);
      window.localStorage.setItem(TICKER_KEY, propertyTicker);
      setConversationId(id);
    } catch {
      setConversationId(generateId());
    }
  }, [propertyTicker]);

  // Makes an externally-supplied session id (selected from the history panel) the
  // active conversation for the current property, without requiring a remount.
  const loadConversationId = useCallback(
    (id: string) => {
      try {
        window.localStorage.setItem(ID_KEY, id);
        window.localStorage.setItem(TICKER_KEY, propertyTicker);
      } catch {
        // Persistence failed — still honor the caller's intent in memory below;
        // it just won't survive a refresh.
      }
      setConversationId(id);
    },
    [propertyTicker]
  );

  return [conversationId, resetConversationId, loadConversationId];
}
